import { z } from 'zod';
import {
  type AdminLoginInput,
  type AdminSession,
  adminSessionSchema,
  type ApiErrorBody,
  type ApiErrorCode,
  apiErrorBodySchema,
  type BulkProductActionInput,
  type BulkProductActionResult,
  bulkProductActionResultSchema,
  type BulkStockAdjustInput,
  type CreateProductInput,
  type DashboardStats,
  dashboardStatsSchema,
  type Order,
  type OrderListQuery,
  orderSchema,
  type Paginated,
  paginatedSchema,
  type Product,
  type ProductListQuery,
  productSchema,
  type StatsQuery,
  type UpdateOrderInput,
  type UpdateProductInput,
} from '@kon/shared';
import { ADMIN_ROOT } from './env';
import { notifyUnauthorized, readSession } from './session';

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly details?: Record<string, string[]>;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    if (body.details) this.details = body.details;
  }
}

/** Bearer header from the stored session, if any. */
function authHeaders(): Record<string, string> {
  const session = readSession();
  return session ? { authorization: `Bearer ${session.token}` } : {};
}

async function parseEnvelope<S extends z.ZodTypeAny>(
  res: Response,
  schema: S,
  options: { isLogin?: boolean } = {},
): Promise<z.infer<S>> {
  // An expired/revoked session logs the UI out (except on the login call
  // itself, where 401 just means wrong credentials).
  if (res.status === 401 && !options.isLogin) {
    notifyUnauthorized();
  }

  let json: unknown;
  try {
    json = await res.json();
  } catch {
    throw new ApiError(res.status, { code: 'INTERNAL', message: 'Malformed server response' });
  }

  const envelope = z
    .object({ data: z.unknown(), error: apiErrorBodySchema.nullable() })
    .safeParse(json);
  if (!envelope.success) {
    throw new ApiError(res.status, { code: 'INTERNAL', message: 'Unexpected response shape' });
  }
  if (envelope.data.error) throw new ApiError(res.status, envelope.data.error);

  const parsed = schema.safeParse(envelope.data.data);
  if (!parsed.success) {
    throw new ApiError(res.status, { code: 'INTERNAL', message: 'Response failed validation' });
  }
  return parsed.data;
}

interface JsonRequest<S extends z.ZodTypeAny> {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  schema: S;
  signal?: AbortSignal;
}

async function request<S extends z.ZodTypeAny>(
  path: string,
  options: JsonRequest<S>,
): Promise<z.infer<S>> {
  const res = await fetch(`${ADMIN_ROOT}${path}`, {
    method: options.method ?? 'GET',
    credentials: 'include',
    headers: {
      ...authHeaders(),
      ...(options.body !== undefined ? { 'content-type': 'application/json' } : {}),
    },
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  });
  return parseEnvelope(res, options.schema);
}

function toParams(query: Record<string, string | number | boolean | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === '') continue;
    params.set(key, String(value));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

export const adminApi = {
  /* auth */
  async login(input: AdminLoginInput): Promise<AdminSession> {
    const res = await fetch(`${ADMIN_ROOT}/auth/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
    });
    return parseEnvelope(res, adminSessionSchema, { isLogin: true });
  },
  me(signal?: AbortSignal): Promise<{ username: string }> {
    return request('/auth/me', {
      schema: z.object({ username: z.string() }),
      ...(signal ? { signal } : {}),
    });
  },

  /* products */
  listProducts(query: Partial<ProductListQuery>, signal?: AbortSignal): Promise<Paginated<Product>> {
    return request(`/products${toParams(query)}`, {
      schema: paginatedSchema(productSchema),
      ...(signal ? { signal } : {}),
    });
  },
  getProduct(id: string, signal?: AbortSignal): Promise<Product> {
    return request(`/products/${id}`, { schema: productSchema, ...(signal ? { signal } : {}) });
  },
  createProduct(input: CreateProductInput): Promise<Product> {
    return request('/products', { method: 'POST', body: input, schema: productSchema });
  },
  updateProduct(id: string, input: UpdateProductInput): Promise<Product> {
    return request(`/products/${id}`, { method: 'PATCH', body: input, schema: productSchema });
  },
  deleteProduct(id: string): Promise<{ deleted: boolean }> {
    return request(`/products/${id}`, {
      method: 'DELETE',
      schema: z.object({ deleted: z.boolean() }),
    });
  },
  bulkAction(input: BulkProductActionInput): Promise<BulkProductActionResult> {
    return request('/products/bulk-action', {
      method: 'POST',
      body: input,
      schema: bulkProductActionResultSchema,
    });
  },
  bulkStock(input: BulkStockAdjustInput): Promise<Product[]> {
    return request('/products/bulk-stock', {
      method: 'POST',
      body: input,
      schema: z.array(productSchema),
    });
  },
  async uploadImage(id: string, file: File): Promise<Product> {
    const res = await fetch(`${ADMIN_ROOT}/products/${id}/image`, {
      method: 'POST',
      credentials: 'include',
      headers: { ...authHeaders(), 'content-type': file.type },
      body: file,
    });
    return parseEnvelope(res, productSchema);
  },

  /* orders */
  listOrders(query: Partial<OrderListQuery>, signal?: AbortSignal): Promise<Paginated<Order>> {
    return request(`/orders${toParams(query)}`, {
      schema: paginatedSchema(orderSchema),
      ...(signal ? { signal } : {}),
    });
  },
  getOrder(id: string, signal?: AbortSignal): Promise<Order> {
    return request(`/orders/${id}`, { schema: orderSchema, ...(signal ? { signal } : {}) });
  },
  updateOrder(id: string, input: UpdateOrderInput): Promise<Order> {
    return request(`/orders/${id}`, { method: 'PATCH', body: input, schema: orderSchema });
  },

  /* stats */
  getStats(query: StatsQuery, signal?: AbortSignal): Promise<DashboardStats> {
    return request(`/stats${toParams(query)}`, {
      schema: dashboardStatsSchema,
      ...(signal ? { signal } : {}),
    });
  },
};
